import {
  getMemberDetail,
  MemberDetailApiError,
  type MemberDetailContract,
} from '@urbanik/api-client';
import {
  AppFooter,
  Button,
  CanonicalCard,
  CanonicalModal,
  CanonicalPageHeader,
  CanonicalTimeline,
  InfoPanel,
  PageActions,
  ProfileSummary,
  QuickActionCard,
  StatePanel,
  StatusBadge,
  SummaryList,
  SummaryListItem,
  Tabs,
  canonicalStates,
  formatDateTime,
} from '@urbanik/ui';
import {
  ArrowLeft,
  CalendarClock,
  CalendarDays,
  ChartNoAxesCombined,
  FileText,
  History,
  House,
  Landmark,
  ListTree,
  Mail,
  MapPinned,
  MessageCircle,
  Pencil,
  Phone,
  Power,
  ReceiptText,
  UsersRound,
  WalletCards,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
import { MemberAddressLocation } from './MemberAddressLocation';
import { MemberAliquotsPanel } from './MemberAliquotsPanel';
import { MemberAdmissionPaymentsPanel } from './MemberAdmissionPaymentsPanel';
import { MemberFinancialAccount } from './MemberFinancialAccount';
import { MemberPhotoEditor } from './MemberPhotoEditor';
import { ObligationPortfolioView } from './ObligationPortfolioView';
import './member-detail.css';
import { MemberStatusEditor } from './MemberStatusEditor';

type ViewState = 'loading' | 'error' | 'forbidden' | 'empty' | 'success';
export function MemberDetailView({
  memberId,
  canUpdate = false,
  canChangeStatus = false,
  canReadPhoto = false,
  canReadLocation = false,
  canReadPortfolio = false,
}: {
  canReadPortfolio?: boolean;
  memberId: string;
  canUpdate?: boolean;
  canReadPhoto?: boolean;
  canManagePhoto?: boolean;
  canChangeStatus?: boolean;
  canReadLocation?: boolean;
  canManageLocation?: boolean;
  canReadHousehold?: boolean;
  canManageHousehold?: boolean;
  canReadHouseholdPhotos?: boolean;
  canManageHouseholdPhotos?: boolean;
  canReadUpdateAuthorizations?: boolean;
  canManageUpdateAuthorizations?: boolean;
}) {
  const [state, setState] = useState<ViewState>('loading');
  const [member, setMember] = useState<MemberDetailContract | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [tab, setTab] = useState(() => {
    const tabs = new Set([
      'summary',
      'aliquots',
      'account',
      'payments',
      'settlements',
      'terrain',
      'documents',
      'observations',
      'history',
      'beneficiaries',
    ]);
    const query = window.location.hash.split('?')[1] ?? '';
    const requested = new URLSearchParams(query).get('tab');
    return requested && tabs.has(requested) ? requested : 'summary';
  });
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    getMemberDetail(apiBase, memberId, controller.signal)
      .then((value) => {
        setMember(value.member);
        setState('success');
      })
      .catch((problem) => {
        if (controller.signal.aborted) return;
        if (problem instanceof MemberDetailApiError && problem.kind === 'unauthorized') {
          location.hash = '#login';
          return;
        }
        if (problem instanceof MemberDetailApiError && problem.kind === 'forbidden')
          setState('forbidden');
        else if (
          problem instanceof MemberDetailApiError &&
          (problem.kind === 'not-found' || problem.kind === 'validation')
        )
          setState('empty');
        else setState('error');
      });
    return () => controller.abort();
  }, [attempt, memberId]);
  if (state !== 'success' || !member) {
    const definition = canonicalStates.find((item) => item.kind === state)!;
    return (
      <div className="shell-page member-detail-page">
        <StatePanel
          state={definition}
          onAction={() =>
            state === 'empty' ? (location.hash = '#socios') : setAttempt((value) => value + 1)
          }
        />
      </div>
    );
  }
  const label = member.status === 'active' ? 'Activo' : 'Inactivo';
  const targetLabel = member.status === 'active' ? 'Inactivar' : 'Gestionar estado de';
  // State changes use the shared administrative editor.
  return (
    <div className="shell-page member-detail-page">
      <CanonicalPageHeader
        title="Socio"
        description="Consulta y administra la información disponible del socio, su contacto, ubicación, hogar y trazabilidad."
        actions={
          <PageActions>
            <Button
              icon={<ArrowLeft aria-hidden="true" />}
              onClick={() => (location.hash = '#socios')}
            >
              Volver a socios
            </Button>
            {canUpdate ? (
              <Button
                icon={<Pencil aria-hidden="true" />}
                onClick={() => (location.hash = `#socios/${member.id}/editar`)}
              >
                Editar socio
              </Button>
            ) : null}
            {canChangeStatus ? (
              <Button
                variant="primary"
                icon={<Power aria-hidden="true" />}
                onClick={() => setOpen(true)}
              >
                {targetLabel} socio
              </Button>
            ) : null}
          </PageActions>
        }
      />
      <div
        className={`member-detail-overview${tab === 'account' || tab === 'payments' ? ' member-detail-overview--financial' : ''}`}
      >
        <section className="member-detail-primary" aria-label="Expediente del socio">
          <ProfileSummary
            avatar={
              <MemberPhotoEditor
                memberId={member.id}
                name={`${member.givenNames} ${member.familyNames}`}
                canRead={canReadPhoto}
                canManage={false}
              />
            }
            name={`${member.givenNames} ${member.familyNames}`}
            code={`Cédula ${member.nationalId}`}
            status={
              <StatusBadge tone={member.status === 'active' ? 'success' : 'neutral'}>
                {label}
              </StatusBadge>
            }
            items={[
              { label: 'Correo', value: member.email ?? 'No registrado', icon: Mail },
              { label: 'Teléfono', value: member.primaryPhone ?? 'No registrado', icon: Phone },
              {
                label: 'Registro en el sistema',
                value: formatDateTime(member.createdAt),
                icon: CalendarDays,
              },
              {
                label: 'Última actualización',
                value: formatDateTime(member.updatedAt),
                icon: History,
              },
            ]}
          />
          <Tabs
            activeId={tab}
            onChange={setTab}
            label="Secciones del expediente"
            items={[
              { id: 'summary', label: 'Resumen', icon: <House aria-hidden="true" /> },
              { id: 'aliquots', label: 'Cupos', icon: <ListTree aria-hidden="true" /> },
              {
                id: 'account',
                label: 'Estado de cuenta',
                icon: <WalletCards aria-hidden="true" />,
              },
              { id: 'payments', label: 'Pagos', icon: <ReceiptText aria-hidden="true" /> },
              { id: 'settlements', label: 'Liquidaciones', icon: <Landmark aria-hidden="true" /> },
              { id: 'terrain', label: 'Terrenos', icon: <MapPinned aria-hidden="true" /> },
              { id: 'documents', label: 'Documentos', icon: <FileText aria-hidden="true" /> },
              {
                id: 'observations',
                label: 'Observaciones',
                icon: <MessageCircle aria-hidden="true" />,
              },
              {
                id: 'history',
                label: 'Historial',
                icon: <History aria-hidden="true" />,
                count: member.history?.length ?? 0,
              },
              {
                id: 'beneficiaries',
                label: 'Beneficiarios',
                icon: <UsersRound aria-hidden="true" />,
              },
            ]}
          />
          {tab === 'summary' ? (
            <div role="tabpanel" className="member-detail-panel-stack">
              <div className="member-detail-dashboard-grid">
                <div className="member-detail-dashboard-stack">
                  <SummaryList title="Información personal">
                    <SummaryListItem label="Nombres" value={member.givenNames} />
                    <SummaryListItem label="Apellidos" value={member.familyNames} />
                    <SummaryListItem label="Cédula" value={member.nationalId} />
                    <SummaryListItem label="Correo" value={member.email ?? 'No registrado'} />
                    <SummaryListItem
                      label="Teléfono"
                      value={member.primaryPhone ?? 'No registrado'}
                    />
                  </SummaryList>
                  <SummaryList title="Información financiera rápida">
                    <SummaryListItem label="Aportes totales" value="Pendiente de integración" />
                    <SummaryListItem label="Total pagado" value="Pendiente de integración" />
                    <SummaryListItem label="Saldo pendiente" value="Pendiente de integración" />
                    <SummaryListItem label="Último pago" value="No disponible" />
                  </SummaryList>
                </div>
                <section
                  className="member-detail-module-card member-detail-timeline"
                  aria-labelledby="member-timeline-title"
                >
                  <h2 id="member-timeline-title">Línea de tiempo</h2>
                  <CanonicalTimeline
                    empty="No existen eventos registrados."
                    events={(member.history ?? []).map((item) => ({
                      id: item.id,
                      title: item.status === 'active' ? 'Socio activado' : 'Socio inactivado',
                      description: item.reason,
                      date: formatDateTime(item.occurredAt),
                      actor: `Evento ${item.sequence}`,
                      icon: (
                        <StatusBadge tone={item.status === 'active' ? 'success' : 'neutral'}>
                          {item.status === 'active' ? 'Activo' : 'Inactivo'}
                        </StatusBadge>
                      ),
                    }))}
                  />
                </section>
                <section
                  className="member-detail-module-card"
                  aria-labelledby="member-documents-title"
                >
                  <h2 id="member-documents-title">Documentos</h2>
                  <InfoPanel
                    title="Expediente documental preparado"
                    icon={<FileText aria-hidden="true" />}
                  >
                    No existe todavía un contrato de documentos para este socio.
                  </InfoPanel>
                </section>
              </div>
              <section
                className="member-detail-module-card member-detail-family-summary"
                aria-labelledby="member-family-title"
              >
                <h2 id="member-family-title">Grupo familiar</h2>
                <InfoPanel title="Información familiar" icon={<UsersRound aria-hidden="true" />}>
                  Consulta los integrantes desde la pestaña Beneficiarios. El registro y la edición
                  se administran desde Editar socio.
                </InfoPanel>
              </section>
            </div>
          ) : null}
          {tab === 'aliquots' ? <MemberAliquotsPanel memberId={member.id} /> : null}
          {tab === 'account' ? (
            canReadPortfolio ? (
              <div
                role="tabpanel"
                className="member-detail-panel-stack member-detail-account-panel"
              >
                <MemberFinancialAccount memberId={member.id} />
                <CanonicalCard
                  title="Detalles de movimientos"
                  icon={<ListTree aria-hidden="true" />}
                >
                  <ObligationPortfolioView memberId={member.id} />
                </CanonicalCard>
              </div>
            ) : (
              <StatePanel state={canonicalStates.find((item) => item.kind === 'forbidden')!} />
            )
          ) : null}
          {tab === 'payments' ? (
            <div className="member-detail-panel-stack">
              <MemberFinancialAccount memberId={member.id} mode="payments" />
              <MemberAdmissionPaymentsPanel memberId={member.id} />
            </div>
          ) : null}
          {tab === 'settlements' ? (
            <section role="tabpanel" className="member-detail-module-card">
              <InfoPanel
                title="Módulo no implementado"
                icon={<WalletCards aria-hidden="true" />}
                tone="warning"
              >
                Pagos y liquidaciones permanecen fuera del alcance autorizado.
              </InfoPanel>
            </section>
          ) : null}
          {tab === 'terrain' ? (
            <div role="tabpanel" className="member-detail-panel-stack">
              {canReadLocation ? (
                <MemberAddressLocation memberId={memberId} canManage={false} />
              ) : null}
              <InfoPanel
                title="Terreno asignado"
                icon={<MapPinned aria-hidden="true" />}
                tone="warning"
              >
                La estructura está lista; todavía no existe un contrato de terrenos asignados.
              </InfoPanel>
            </div>
          ) : null}
          {tab === 'documents' ? (
            <section role="tabpanel" className="member-detail-module-card">
              <MemberAdmissionPaymentsPanel memberId={member.id} mode="documents" />
            </section>
          ) : null}
          {tab === 'observations' ? (
            <section role="tabpanel" className="member-detail-module-card">
              <InfoPanel
                title="Observaciones"
                icon={<MessageCircle aria-hidden="true" />}
                tone="warning"
              >
                La estructura está lista para registrar observaciones cuando exista el contrato
                correspondiente.
              </InfoPanel>
            </section>
          ) : null}
          {tab === 'beneficiaries' ? (
            <section role="tabpanel" className="member-detail-module-card">
              <InfoPanel
                title="Grupo familiar y beneficiarios"
                icon={<UsersRound aria-hidden="true" />}
              >
                La sección queda preparada para mostrar aquí el resumen familiar conectado. El
                registro y la edición se realizan exclusivamente desde Editar socio.
              </InfoPanel>
            </section>
          ) : null}
          {tab === 'history' ? (
            <section role="tabpanel" className="member-history">
              <CanonicalTimeline
                empty="No existen cambios de estado registrados."
                events={(member.history ?? []).map((item) => ({
                  id: item.id,
                  title: item.status === 'active' ? 'Socio activado' : 'Socio inactivado',
                  description: item.reason,
                  date: formatDateTime(item.occurredAt),
                  actor: `Evento ${item.sequence}`,
                  icon: (
                    <StatusBadge tone={item.status === 'active' ? 'success' : 'neutral'}>
                      {item.status === 'active' ? 'Activo' : 'Inactivo'}
                    </StatusBadge>
                  ),
                }))}
              />
            </section>
          ) : null}
        </section>
        <aside className="member-detail-rail" aria-label="Resumen y acciones">
          {tab === 'account' ? (
            <>
              <CanonicalCard title="Resumen de cuenta" icon={<WalletCards aria-hidden="true" />}>
                <InfoPanel title="Componente preparado">
                  Consolidará deuda, pagos aplicados y saldo pendiente cuando existan sus módulos.
                </InfoPanel>
              </CanonicalCard>
              <CanonicalCard
                title="Próximos vencimientos"
                icon={<CalendarClock aria-hidden="true" />}
              >
                <InfoPanel title="Agenda preparada">
                  Listará fechas e importes exigibles cuando el módulo esté conectado.
                </InfoPanel>
              </CanonicalCard>
              <CanonicalCard title="Acciones rápidas" icon={<ReceiptText aria-hidden="true" />}>
                <InfoPanel title="Acciones preparadas">
                  Los accesos financieros se habilitarán únicamente al aprobar sus módulos.
                </InfoPanel>
              </CanonicalCard>
            </>
          ) : (
            <>
              <SummaryList title="Resumen financiero">
                <SummaryListItem label="Total aportes" value="Pendiente de integración" />
                <SummaryListItem label="Total pagado" value="Pendiente de integración" />
                <SummaryListItem label="Saldo pendiente" value="Pendiente de integración" />
                <SummaryListItem label="Mora actual" value="No disponible" />
                <SummaryListItem label="Último pago" value="No disponible" />
              </SummaryList>
              <section className="member-detail-module-card">
                <h2>Terreno asignado</h2>
                <InfoPanel title="Sin conexión de terrenos" icon={<MapPinned aria-hidden="true" />}>
                  Componente preparado para mostrar ubicación, manzana, lote, área y fecha de
                  asignación.
                </InfoPanel>
              </section>
              <section className="member-detail-quick" aria-labelledby="member-quick-title">
                <h2 id="member-quick-title">Acciones rápidas</h2>
                <div>
                  {canUpdate ? (
                    <QuickActionCard
                      icon={<Pencil aria-hidden="true" />}
                      title="Editar socio"
                      description="Actualizar datos autorizados"
                      onClick={() => (location.hash = `#socios/${member.id}/editar`)}
                    />
                  ) : null}
                  {canChangeStatus ? (
                    <QuickActionCard
                      icon={<Power aria-hidden="true" />}
                      title={`${targetLabel} socio`}
                      description="Registrar cambio de estado"
                      onClick={() => setOpen(true)}
                    />
                  ) : null}
                </div>
              </section>
              <section className="member-detail-module-card">
                <h2>Observaciones</h2>
                <InfoPanel
                  title="Sin observaciones registradas"
                  icon={<MessageCircle aria-hidden="true" />}
                >
                  Componente preparado para la futura conexión del registro de observaciones.
                </InfoPanel>
              </section>
            </>
          )}
        </aside>
      </div>
      <AppFooter
        description="Sistema integral de gestión comunitaria"
        version="Versión 1.0.0"
        links={[
          { label: 'Dashboard', href: '#dashboard' },
          { label: 'Privacidad', href: '#fundamentos' },
        ]}
      />
      <CanonicalModal
        open={open}
        size="medium"
        eyebrow="Cambio de estado"
        title={`${targetLabel} socio`}
        description={`Estado actual: ${label}. El cambio quedará registrado en el historial.`}
        onClose={() => setOpen(false)}
      >
        {open ? <MemberStatusEditor member={member} onChanged={setMember} /> : null}
      </CanonicalModal>
    </div>
  );
}
